import React from "react";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "../stores/auth.store";
import { useAuthPermissions } from "../hooks/useAuthPermissions";
import PermissionDeniedGate from "./PermissionDeniedGate";

interface AuthRouteProps {
  children: React.ReactNode;
  /** Full permission string (when not org-scoped). */
  requiredPermission?: string;
  /** Build permission from the active organization id. */
  orgScopedPermission?: (orgId: string) => string;
  legacyRoles?: readonly string[];
}

/**
 * Authenticated route with optional permission gate (dual-mode legacy fallback).
 */
const AuthRoute: React.FC<AuthRouteProps> = ({
  children,
  requiredPermission,
  orgScopedPermission,
  legacyRoles,
}) => {
  const { isAuthenticated } = useAuthStore();
  const { can, orgId, isPlatformAdmin } = useAuthPermissions();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isPlatformAdmin && (orgScopedPermission || requiredPermission)) {
    const hasOrgAccess =
      Boolean(orgScopedPermission && orgId) &&
      can(orgScopedPermission!(orgId!), legacyRoles);
    const hasPlatformAccess = Boolean(
      requiredPermission && can(requiredPermission, legacyRoles),
    );

    if (!hasOrgAccess && !hasPlatformAccess) {
      return <PermissionDeniedGate />;
    }
  }

  return <>{children}</>;
};

export default AuthRoute;
